import type { MyBrandKpiCards, MyBrandKpiCard, MyBrandKpiMetric, NorthStarKpi } from '@nexus/contracts';

export interface CardDescriptor {
  index:       number;
  label:       string;
  metric:      (northStar: NorthStarKpi) => MyBrandKpiMetric;
  card:        (data: MyBrandKpiCards, northStar: NorthStarKpi) => MyBrandKpiCard;
  // Value format for the metric. Mirrors the format set in mapRowToKpiCards so the
  // chart can format axes/tooltips from the standalone time series alone (no cards object).
  format:      (northStar: NorthStarKpi) => MyBrandKpiCard['format'];
  colorToken:  string;              // CSS var name, e.g. '--chart-spend'
  aggregation: 'sum' | 'average';   // bucket roll-up: sum for additive totals, average for rates/ratios
}

export const CARD_DESCRIPTORS: readonly CardDescriptor[] = [
  {
    index:       0,
    label:       'Spend',
    metric:      () => 'spend',
    card:        (d) => d.spend,
    format:      () => 'currency',
    colorToken:  '--chart-spend',
    aggregation: 'sum',
  },
  {
    index:       1,
    label:       'North Star',
    metric:      (ns) => ns === 'roas' ? 'northStarRoas' : ns === 'cpa' ? 'northStarCpa' : 'northStarCustom',
    card:        (d, ns) => d.northStar[ns],
    format:      (ns) => ns === 'roas' ? 'ratio' : 'currency',
    colorToken:  '--chart-roas',
    aggregation: 'average', // roas/cpa/custom are all rates — summing across days inflates
  },
  {
    index:       2,
    label:       'Creative Volume',
    metric:      () => 'creativeVolume',
    card:        (d) => d.creativeVolume,
    format:      () => 'count',
    colorToken:  '--chart-creative-volume',
    aggregation: 'sum',
  },
  {
    index:       3,
    label:       'Creative Concentration',
    metric:      () => 'creativeConcentration',
    card:        (d) => d.creativeConcentration,
    format:      () => 'percent',
    colorToken:  '--chart-creative-concentration',
    aggregation: 'average',
  },
  {
    index:       4,
    label:       'Stick Rate',
    metric:      () => 'stickRate',
    card:        (d, ns) => d.stickRate[ns],
    format:      () => 'percent',
    colorToken:  '--chart-stick-rate',
    aggregation: 'average',
  },
  {
    index:       5,
    label:       'Winners',
    metric:      () => 'winners',
    card:        (d, ns) => d.winners[ns],
    format:      () => 'count',
    colorToken:  '--chart-winners',
    aggregation: 'sum',
  },
];

export function resolveColorToken(desc: CardDescriptor, northStar: NorthStarKpi): string {
  if (desc.index === 1) {
    // --chart-custom does not exist in the design system token set.
    // northStarCustom intentionally shares --chart-cpa (matches existing METRIC_TOKEN in MyBrandKpiCards).
    return northStar === 'roas' ? '--chart-roas' : '--chart-cpa';
  }
  return desc.colorToken;
}

export interface CatalogColorEntry {
  colorVar: string;
}

const CSS_VAR_NAME_RE = /^var\((--[\w-]+)\)$/;

export function cssVarNameFromColorVar(colorVar: string): string | null {
  const match = CSS_VAR_NAME_RE.exec(colorVar);
  return match?.[1] ?? null;
}

/** Catalog KPI name for a dashboard card, or null when the card has no counterpart. */
export function dashboardCatalogName(
  desc: CardDescriptor,
  northStar: NorthStarKpi,
): string | null {
  switch (desc.index) {
    case 0:
      return 'Spend';
    case 1:
      if (northStar === 'roas') return 'ROAS';
      if (northStar === 'cpa') return 'CPA';
      return null;
    case 2:
      return 'Creatives';
    case 4:
      return 'Stick Rate';
    default:
      return null;
  }
}

export function resolveDashboardColorToken(
  desc: CardDescriptor,
  northStar: NorthStarKpi,
  catalogById?: Readonly<Record<string, CatalogColorEntry>>,
): string {
  const name = dashboardCatalogName(desc, northStar);
  if (name && catalogById) {
    const colorVar = catalogById[name]?.colorVar;
    const token = colorVar ? cssVarNameFromColorVar(colorVar) : null;
    if (token) return token;
  }
  return resolveColorToken(desc, northStar);
}

export interface MyBrandCardView {
  index: number;
  label: string;
  value: number;
  previousValue: number;
  format: MyBrandKpiCard['format'];
  colorToken: string;
}

export function buildMyBrandCardViews(
  data: MyBrandKpiCards,
  northStar: NorthStarKpi,
  catalogById?: Readonly<Record<string, CatalogColorEntry>>,
): MyBrandCardView[] {
  return CARD_DESCRIPTORS.map((desc) => {
    const card = desc.card(data, northStar);
    return {
      index: desc.index,
      label: card.label,
      value: card.value,
      previousValue: card.previousValue,
      format: card.format,
      colorToken: resolveDashboardColorToken(desc, northStar, catalogById),
    };
  });
}

